import { Button, View } from "@/components/ui";
import { useAuthState } from "@/store/auth";
import { AuthModals, LoginType } from "@/types/users";
import { EmailForm } from "../email-login-modal/email-form";
import { useEmailLoginForm } from "@/store/auth/forms/useEmailLoginForm";
import { useSignInStore } from "@/store/auth/signIn";
import { cn } from "@/lib/utils";
import { useRoute } from "@/components/shared/router";
import { useEffect } from "react";
import { Typography } from "../../typography";
import { sendAnalyticData } from "@/lib/monitoring/analytics";
import { SignInProviderEnum } from "@/__generated__/graphql";
import { useAuth } from "../../auth-provider";
import { PosthogAnalytics } from "@/lib/monitoring/posthog";

export const EmailLoginContent = ({
  isModal = true,
}: {
  isModal?: boolean;
}) => {
  const { replace } = useRoute();
  const formReturn = useEmailLoginForm();
  const { reset, defaultValues } = formReturn;

  const { login } = useAuth();
  const { showModal, setType, setLoginByModal, setUserInfo } = useAuthState();

  const { loading, fetch: emailLogin, error } = useSignInStore();

  useEffect(() => {
    setLoginByModal(isModal);
    setType(LoginType.EmailLogin);
  }, []);

  const onSubmitForm = (data: { email: string; password: string }) => {
    emailLogin(
      {
        requestPayload: {
          input: {
            email: data.email,
            password: data.password,
          },
        },
      },
      { selfHandleError: true },
    ).then((res) => {
      const data = res.data;
      if (res.error || !data || !data.userInfo) return;
      setUserInfo(data.userInfo);
      login(data.loginArgs);
      sendAnalyticData({
        name: "user_sign_in",
        values: {
          method: SignInProviderEnum.Password,
        },
      });

      showModal();
    });
  };
  const openForgotPasswordModal = () => {
    if (isModal) showModal(AuthModals.EMAIL_LOGIN, AuthModals.FORGOT_PASSWORD);
    else showModal(AuthModals.FORGOT_PASSWORD);
  };
  const onGoToLogin = () => {
    reset(defaultValues);
    if (isModal) showModal(AuthModals.LOGIN);
    else setType(LoginType.Login);
  };
  const onGoToSingUp = () => {
    reset(defaultValues);
    PosthogAnalytics.track("user_sign_up");
    if (isModal) showModal(AuthModals.SIGN_UP);
    else replace({ pageKey: "signup" });
  };

  return (
    <View className={cn("flex flex-col", isModal ? "gap-2" : "gap-4")}>
      <View className="pb-3">
        <Typography variant="label-md">Sign In</Typography>
        <Typography variant="body-sm" color="foreground-intermediate">
          To continue the booking process, you need to login or create a new
          account
        </Typography>
      </View>
      <EmailForm
        formReturn={formReturn}
        loading={loading}
        onSubmitForm={onSubmitForm}
        errorMessage={error || ""}
        onForgotPassword={openForgotPasswordModal}
      />

      <Button
        variant="secondary"
        color="CTA2"
        rounded="full"
        className="mt-2"
        onClick={onGoToLogin}
        children="Sign in using Phone Number"
      />
      <View className="flex flex-row justify-center gap-2 pt-4">
        <Typography variant="body-md" color="secondary-text">
          Don't have an account?
        </Typography>
        <Typography
          variant="body-md"
          className="cursor-pointer color-link"
          onClick={onGoToSingUp}
        >
          Sign Up
        </Typography>
      </View>
    </View>
  );
};
